import {
  CoreApi,
  HandleType,
  Product,
  ProductIdentifiers,
  ProductInfoComponent,
  ProductPartial,
  ProductType
} from '@inter-ikea-storageone/range';
import { ComponentIds, Entity } from '@inter-ikea-core/core';
import * as R from 'ramda';
import { getAllowedHandleTypes } from '../utils/frontLayouts';

export const matchOrientation = (coreApi: CoreApi, selectedProduct: Product) => (
  product: Product
) => {
  return (
    selectedProduct.coreProductId.includes(ProductIdentifiers.MIRRORED_PRODUCT) ===
    product.coreProductId.includes(ProductIdentifiers.MIRRORED_PRODUCT)
  );
};

export const matchDimensions = (coreApi: CoreApi, selectedProduct: Product) => (
  product: Product
) => {
  if (
    selectedProduct.type === ProductType.PAX_CORNER_SOLUTION_ASM &&
    !matchOrientation(coreApi, selectedProduct)(product)
  ) {
    return false;
  }

  return R.equals(product.communicatedDimensions, selectedProduct.communicatedDimensions);
};

export const matchHeight = (coreApi: CoreApi, selectedProduct: Product | ProductPartial) => (
  product: Product | ProductPartial
) => product.communicatedDimensions.height === selectedProduct.communicatedDimensions.height;

export const matchWidth = (coreApi: CoreApi, selectedProduct: Product | ProductPartial) => (
  product: Product | ProductPartial
) => product.communicatedDimensions.width === selectedProduct.communicatedDimensions.width;

export const matchDepth = (coreApi: CoreApi, selectedProduct: Product | ProductPartial) => (
  product: Product | ProductPartial
) => product.communicatedDimensions.depth === selectedProduct.communicatedDimensions.depth;

// Match product that fits up to a certain height
export const matchInexactHeight = (coreApi: CoreApi, selectedProduct: Product | ProductPartial) => (
  product: Product | ProductPartial
) => product.communicatedDimensions.height <= selectedProduct.communicatedDimensions.height;

// Match product that fits up to a certain width
export const matchInexactWidth = (coreApi: CoreApi, selectedProduct: Product | ProductPartial) => (
  product: Product | ProductPartial
) => product.communicatedDimensions.width <= selectedProduct.communicatedDimensions.width;

// Match product that fits up to a certain depth
export const matchInexactDepth = (coreApi: CoreApi, selectedProduct: Product | ProductPartial) => (
  product: Product | ProductPartial
) => {
  return product.communicatedDimensions.depth <= selectedProduct.communicatedDimensions.depth;
};

export const matchType = (coreApi: CoreApi, selectedProduct: Product | ProductPartial) => (
  product: Product | ProductPartial
) => {
  return selectedProduct.type === product.type;
};

export const matchHandleSizeToFront = (
  coreApi: CoreApi,
  selectedProduct: Product | ProductPartial,
  selectedEntity?: Entity
) => (product: Product | ProductPartial) => {
  const handleDoorConnectorEdgesDiff = 40;
  if (selectedEntity?.hasComponent(ComponentIds.DRAWER_COMPONENT_ID)) {
    return product.communicatedDimensions.height <= selectedProduct.communicatedDimensions.width;
  } else if (selectedEntity?.hasComponent(ComponentIds.DOOR_COMPONENT_ID)) {
    return (
      product.communicatedDimensions.height <=
      selectedProduct.communicatedDimensions.height - handleDoorConnectorEdgesDiff
    );
  }
  return true;
};

export const matchHandleTypeToFront = (
  coreApi: CoreApi,
  selectedProduct: Product | ProductPartial,
  selectedEntity?: Entity
) => (product: Product | ProductPartial) => {
  if (!selectedEntity) return false;
  const allowedHandleTypes = getAllowedHandleTypes(selectedEntity);
  return (allowedHandleTypes && allowedHandleTypes.includes(product.type as HandleType)) || false;
};

// Match front to current front types on frame
export const matchFrameFrontType = (
  coreApi: CoreApi,
  selectedProduct: Product | ProductPartial,
  selectedEntity?: Entity
) => (product: Product | ProductPartial) => {
  if (!selectedEntity || !selectedEntity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) return true;
  const frontsOnFrame = coreApi.entities
    .getFrameDescendants(selectedEntity)
    .filter(
      child =>
        (child.hasComponent(ComponentIds.DOOR_COMPONENT_ID) ||
          child.hasComponent(ComponentIds.DRAWER_COMPONENT_ID)) &&
        !child.hasComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID)
    );
  if (frontsOnFrame.length === 0) return true;
  const frontTypesOnFrame = frontsOnFrame
    .map(front => front.getComponent<ProductInfoComponent>(ProductInfoComponent.id)?.productType)
    .filter((type): type is ProductType => type !== undefined);
  return frontTypesOnFrame.includes(product.type);
};

export const matchDesign = (coreApi: CoreApi, selectedProduct: Product) => (product: Product) => {
  if (product.meta?.matchesColors) {
    return product.meta.matchesColors.includes(selectedProduct.design);
  }
  return product.design?.split('/')[0] === selectedProduct.design?.split('/')[0];
};
